import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';

const TimelineComponent = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [screenSize, setScreenSize] = useState('large');
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);
  const timelineRef = useRef(null);
  const autoScrollRef = useRef(null);

  const steps = [
    {
      title: "Unified communications",
      description: "All in one video, enterprise VoIP calling, collaboration & messaging"
    },
    {
      title: "Calling",
      description: "SIP trunking hosted PBX, voicemail, & more"
    },
    {
      title: "Meetings",
      description: "Easy video meetings & audio conferencing"
    },
    {
      title: "Messaging",
      description: "Chat via Cisco Webex or Microsoft Teams"
    },
    {
      title: "Call Center solutions",
      description: "Call center options to equip customer service teams"
    },
    {
      title: "Microsoft teams calling",
      description: "End-to-end phone calling system integrated with Microsoft Teams"
    },
    {
      title: "Business Phone Systems & headsets",
      description: "Hardware for your office from market leaders like Poly & Cisco"
    }
  ];

  useEffect(() => {
    // Intersection Observer for scroll-based animation
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
        if (entry.isIntersecting) {
          startAutoScroll();
        } else {
          stopAutoScroll();
        }
      },
      { threshold: 0.2 }
    );

    // Resize handler
    const handleResize = () => {
      const width = window.innerWidth;
      if (width <= 480) {
        setScreenSize('xs');
      } else if (width <= 768) {
        setScreenSize('small');
      } else if (width <= 1024) {
        setScreenSize('medium');
      } else {
        setScreenSize('large');
      }
    };

    // Observe the container
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    // Initial resize
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
      window.removeEventListener('resize', handleResize);
      stopAutoScroll();
    };
  }, []);

  // Auto-scroll functionality
  const startAutoScroll = () => {
    stopAutoScroll();
    autoScrollRef.current = setInterval(() => {
      handleNextStep();
    }, 3000);
  };

  const stopAutoScroll = () => {
    if (autoScrollRef.current) {
      clearInterval(autoScrollRef.current);
    }
  };

  const handlePrevStep = () => {
    setCurrentStep((prev) => (prev > 0 ? prev - 1 : steps.length - 1));
    startAutoScroll();
  };

  const handleNextStep = () => {
    setCurrentStep((prev) => (prev < steps.length - 1 ? prev + 1 : 0));
    startAutoScroll();
  };

  const getResponsiveStyles = () => {
    const baseStyles = {
      container: {
        width: '100%',
        maxWidth: '1200px',
        margin: '0 auto',
        padding: screenSize === 'xs' ? '20px 15px' : '50px 20px',
        backgroundColor: 'white',
        fontFamily: 'Arial, sans-serif',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(50px)',
        transition: 'all 0.8s ease-out'
      },
      header: {
        textAlign: 'center',
        marginBottom: '50px',
        fontFamily: '"Oswald", sans-serif',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transition: 'all 0.8s ease-out 0.2s'
      },
      badge: {
        display: 'inline-block',
        padding: '8px 20px',
        backgroundColor: 'white',
        borderRadius: '30px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        color: '#400684',
        fontWeight: '600',
        fontSize: '16px',
        marginBottom: '15px'
      },
      title: {
        fontSize: screenSize === 'xs' ? '24px' : '40px',
        fontWeight: 'bold',
        color: '#400684',

        marginBottom: '20px'
      },
      timelineContainer: {
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(50px)',
        transition: 'all 0.8s ease-out 0.4s'
      },
      timeline: {
        display: 'flex',
        transition: 'transform 0.5s ease',
        width: '100%'
      },
      stepContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        flex: '0 0 100%',
        padding: screenSize === 'xs' ? '0 15px' : '0 50px',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'scale(1)' : 'scale(0.8)',
        transition: 'all 0.6s ease-out'
      },
      stepNumber: {
        width: screenSize === 'xs' ? '50px' : '70px',
        height: screenSize === 'xs' ? '50px' : '70px',
        borderRadius: '50%',
        backgroundColor: '#400684',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: screenSize === 'xs' ? '20px' : '24px',
        fontWeight: 'bold',
        marginBottom: '15px',
        transform: isVisible ? 'rotateY(0deg)' : 'rotateY(90deg)',
        transition: 'transform 0.6s ease-out'
      },
      stepTitle: {
        fontSize: screenSize === 'xs' ? '20px' : '24px',
        fontWeight: 'bold',
        color: '#1a1a1a',
        marginBottom: '10px',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateX(0)' : 'translateX(-30px)',
        transition: 'all 0.6s ease-out 0.2s'
      },
      stepDescription: {
        fontSize: screenSize === 'xs' ? '14px' : '16px',
        color: '#666',
        maxWidth: '600px',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateX(0)' : 'translateX(30px)',
        transition: 'all 0.6s ease-out 0.4s'
      },
      navigationContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: '30px',
        gap: '20px',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transition: 'all 0.8s ease-out 0.6s'
      },
      navigationButton: {
        backgroundColor: '#400684',
        color: 'white',
        border: 'none',
        borderRadius: '50%',
        width: screenSize === 'xs' ? '40px' : '50px',
        height: screenSize === 'xs' ? '40px' : '50px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'background-color 0.3s ease, transform 0.2s ease',
        '&:hover': {
          transform: 'scale(1.1)'
        }
      },
      indicators: {
        display: 'flex',
        justifyContent: 'center',
        gap: '10px',
        marginTop: '20px',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transition: 'all 0.8s ease-out 0.8s'
      },
      indicator: {
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        backgroundColor: '#CBD5E1',
        cursor: 'pointer',
        transition: 'background-color 0.3s ease, transform 0.2s ease'
      },
      activeIndicator: {
        backgroundColor: '#400684',
        transform: 'scale(1.2)'
      }
    };

    return baseStyles;
  };

  const styles = getResponsiveStyles();

  return (
    <div 
      ref={containerRef}
      style={styles.container}
    >
      <div style={styles.header}>
        <div style={styles.badge}>★ UCaaS & CCaaS ★</div>
        <h2 style={styles.title}>
          One-stop Unified Communication solution
        </h2>
      </div>

      <div style={styles.timelineContainer}>
        <div 
          ref={timelineRef}
          style={{
            ...styles.timeline,
            transform: `translateX(-${currentStep * 100}%)`
          }}
        >
          {steps.map((step, index) => (
            <div 
              key={index} 
              style={{
                ...styles.stepContainer,
                transitionDelay: `${index * 0.1}s`
              }}
            >
              <div style={styles.stepNumber}>{`0${index + 1}`}</div>
              <h3 style={styles.stepTitle}>{step.title}</h3>
              <p style={styles.stepDescription}>{step.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div style={styles.navigationContainer}>
        <button 
          style={styles.navigationButton} 
          onClick={handlePrevStep}
        >
          <ArrowLeft size={24} />
        </button>
        <button 
          style={styles.navigationButton} 
          onClick={handleNextStep}
        >
          <ArrowRight size={24} />
        </button>
      </div>

      <div style={styles.indicators}>
        {steps.map((_, index) => (
          <div
            key={index}
            style={{
              ...styles.indicator,
              ...(index === currentStep ? styles.activeIndicator : {})
            }}
            onClick={() => {
              setCurrentStep(index);
              startAutoScroll();
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default TimelineComponent;